import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowDown, ArrowUp } from "lucide-react";
import { BarraSchede, LIVELLI, TIPI_PIPELINE, TIPI_FASE, useCrmMe, haFlagCrm, errore, useAziendaCrm, CampoAzienda, TornaIndietro, TitoloCrm } from "./crmComuni";

const SEZIONI = [
  { key: "pipeline", label: "Pipeline", flag: "crm_gestisci_pipeline" },
  { key: "fasi", label: "Fasi della pipeline", flag: "crm_gestisci_fasi" },
  { key: "fonte", label: "Fonti", flag: "crm_gestisci_elenchi" },
  { key: "motivo_perdita", label: "Motivi di perdita", flag: "crm_gestisci_elenchi" },
  { key: "etichetta", label: "Etichette", flag: "crm_gestisci_elenchi" },
];
const azione = async (fn, dopo) => { try { await fn(); dopo(); } catch (e) { toast.error(errore(e)); } };
const scambia = (arr, i, j) => { const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };

export default function CrmImpostazioni() {
  const crmMe = useCrmMe();
  const [cid, setCid, aziende] = useAziendaCrm(crmMe);
  const [sez, setSez] = useState("");
  const visibili = SEZIONI.filter((s) => haFlagCrm(crmMe, cid, s.flag));
  useEffect(() => { if (visibili.length && !visibili.some((s) => s.key === sez)) setSez(visibili[0].key); }, [visibili, sez]);
  if (!crmMe) return null;
  return (
    <div className="space-y-4" data-testid="crm-impostazioni-page">
      <TornaIndietro to="/crm" testo="Torna al CRM" testid="crm-imp-indietro" />
      <div className="flex items-end gap-x-3 gap-y-2 flex-wrap">
        <TitoloCrm sotto="Impostazioni" />
        <CampoAzienda aziende={aziende} cid={cid} onScegli={setCid} />
      </div>
      {!visibili.length ? <Card className="p-8 text-center text-slate-500" data-testid="crm-imp-nessun-permesso">Nessun permesso di gestione CRM.</Card> : <>
        <BarraSchede schede={visibili} attiva={sez} onScegli={setSez} testid="crm-imp-schede" />
        {sez === "pipeline" && <SezPipeline cid={cid} />}
        {sez === "fasi" && <SezFasi cid={cid} />}
        {(sez === "fonte" || sez === "motivo_perdita" || sez === "etichetta") && <SezElenco cid={cid} tipo={sez} />}
      </>}
    </div>
  );
}

function SezPipeline({ cid }) {
  const confirm = useConfirm();
  const [lista, setLista] = useState([]);
  const [nuova, setNuova] = useState({ nome: "", descrizione: "", tipo: "vendita" });
  const [accessi, setAccessi] = useState(null);
  const carica = useCallback(() => api.get(`/crm/pipeline?company_id=${cid}&gestione=true`).then((r) => setLista(r.data)).catch(() => setLista([])), [cid]);
  useEffect(() => { carica(); }, [carica]);
  const crea = () => azione(() => api.post("/crm/pipeline", { company_id: cid, ...nuova }), () => { setNuova({ nome: "", descrizione: "", tipo: "vendita" }); carica(); });
  const ordina = (i, j) => { const a = scambia(lista, i, j); azione(() => Promise.all(a.map((p, k) => api.put(`/crm/pipeline/${p.id}`, { ordine: k + 1 }))), carica); };
  const elimina = async (p) => { if (await confirm({ title: "Elimina pipeline", message: `Eliminare «${p.nome}» con le sue fasi e i suoi accessi?` })) azione(() => api.delete(`/crm/pipeline/${p.id}`), carica); };
  return (
    <Card className="p-4 space-y-3" data-testid="crm-imp-pipeline">
      {lista.map((p, i) => (
        <div key={p.id} className={`border rounded p-2 space-y-2 ${p.archiviata ? "bg-slate-50 opacity-70" : ""}`} data-testid={`crm-imp-pipeline-${p.codice}`}>
          <div className="flex flex-wrap gap-2 items-center">
            <Input defaultValue={p.nome} className="w-56" onBlur={(e) => e.target.value !== p.nome && azione(() => api.put(`/crm/pipeline/${p.id}`, { nome: e.target.value }), carica)}
              data-testid={`crm-imp-pipeline-nome-${p.codice}`} />
            <Input defaultValue={p.descrizione || ""} placeholder="Descrizione" className="w-64"
              onBlur={(e) => e.target.value !== (p.descrizione || "") && azione(() => api.put(`/crm/pipeline/${p.id}`, { descrizione: e.target.value }), carica)} />
            <Select value={p.tipo} disabled={p.n_opportunita > 0} onValueChange={(v) => azione(() => api.put(`/crm/pipeline/${p.id}`, { tipo: v }), carica)}>
              <SelectTrigger className="w-40" data-testid={`crm-imp-pipeline-tipo-${p.codice}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TIPI_PIPELINE).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-xs text-slate-500 font-mono" data-testid={`crm-imp-pipeline-codice-${p.codice}`}>{p.codice}</span>
            <span className="text-xs text-slate-500">{p.n_opportunita} opportunità{p.archiviata ? " · archiviata" : ""}</span>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => ordina(i, i - 1)}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" disabled={i === lista.length - 1} onClick={() => ordina(i, i + 1)}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setAccessi(accessi === p.id ? null : p.id)} data-testid={`crm-imp-accessi-btn-${p.codice}`}>Accessi</Button>
              {p.n_opportunita === 0 ? <Button size="sm" variant="outline" className="text-rose-600" onClick={() => elimina(p)} data-testid={`crm-imp-pipeline-elimina-${p.codice}`}>Elimina</Button>
                : <Button size="sm" variant="outline" onClick={() => azione(() => api.post(`/crm/pipeline/${p.id}/archivia${p.archiviata ? "?riattiva=true" : ""}`), carica)}
                  data-testid={`crm-imp-pipeline-archivia-${p.codice}`}>{p.archiviata ? "Riattiva" : "Archivia"}</Button>}
            </div>
          </div>
          {accessi === p.id && <Accessi pid={p.id} />}
        </div>
      ))}
      <div className="flex flex-wrap gap-2 items-center border-t pt-3">
        <Input placeholder="Nome nuova pipeline" value={nuova.nome} onChange={(e) => setNuova({ ...nuova, nome: e.target.value })} className="w-56" data-testid="crm-imp-nuova-pipeline-nome" />
        <Input placeholder="Descrizione" value={nuova.descrizione} onChange={(e) => setNuova({ ...nuova, descrizione: e.target.value })} className="w-64" />
        <Select value={nuova.tipo} onValueChange={(v) => setNuova({ ...nuova, tipo: v })}>
          <SelectTrigger className="w-40" data-testid="crm-imp-nuova-pipeline-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TIPI_PIPELINE).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={crea} disabled={!nuova.nome.trim()} data-testid="crm-imp-nuova-pipeline-crea">Crea pipeline</Button>
      </div>
    </Card>
  );
}

function Accessi({ pid }) {
  const [righe, setRighe] = useState([]);
  const carica = useCallback(() => api.get(`/crm/pipeline/${pid}/accessi`).then((r) => setRighe(r.data)).catch((e) => toast.error(errore(e))), [pid]);
  useEffect(() => { carica(); }, [carica]);
  return (
    <div className="border-t pt-2 grid grid-cols-1 md:grid-cols-2 gap-1" data-testid={`crm-imp-accessi-${pid}`}>
      {righe.map((u) => (
        <div key={u.user_id} className="flex items-center justify-between gap-2 text-sm">
          <span>{u.nome}</span>
          <Select value={u.livello} onValueChange={(v) => azione(() => api.put(`/crm/pipeline/${pid}/accessi`, { user_id: u.user_id, livello: v }), carica)}>
            <SelectTrigger className="w-44 h-8" data-testid={`crm-imp-accesso-${u.user_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(LIVELLI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
      {!righe.length && <div className="text-sm text-slate-400">Nessun utente con «visualizza» su questa azienda</div>}
    </div>
  );
}

function SezFasi({ cid }) {
  const confirm = useConfirm();
  const [pipelines, setPipelines] = useState([]);
  const [pid, setPid] = useState("");
  const [fasi, setFasi] = useState([]);
  const [nome, setNome] = useState("");
  useEffect(() => { api.get(`/crm/pipeline?company_id=${cid}&gestione=true`).then((r) => { setPipelines(r.data); setPid(r.data[0]?.id || ""); }).catch(() => setPipelines([])); }, [cid]);
  const carica = useCallback(() => pid && api.get(`/crm/pipeline/${pid}/fasi`).then((r) => setFasi(r.data)).catch(() => setFasi([])), [pid]);
  useEffect(() => { carica(); }, [carica]);
  const normali = fasi.filter((f) => f.tipo === "normale");
  const ordina = (i, j) => azione(() => api.post(`/crm/pipeline/${pid}/fasi/ordine`, { ids: scambia(normali, i, j).map((f) => f.id) }), carica);
  const put = (f, body) => azione(() => api.put(`/crm/fasi/${f.id}`, body), carica);
  // Sprint CRM-2A.1 — tempo massimo e giorni di rilascio: minimo 1 (messaggio nel campo)
  const [errTempi, setErrTempi] = useState({});
  const tempi = (f, tm, gr) => {
    const errato = (tm != null && tm < 1) || (typeof gr === "number" && gr < 1);
    setErrTempi((e) => ({ ...e, [f.id]: errato ? "Minimo 1 giorno" : "" }));
    if (!errato) put(f, { imposta_tempo: true, tempo_massimo_giorni: tm, giorni_rilascio: gr });
  };
  const elimina = async (f) => { if (await confirm({ title: "Elimina fase", message: `Eliminare la fase «${f.nome}»?` })) azione(() => api.delete(`/crm/fasi/${f.id}`), carica); };
  return (
    <Card className="p-4 space-y-3" data-testid="crm-imp-fasi">
      <Select value={pid} onValueChange={setPid}>
        <SelectTrigger className="w-64" data-testid="crm-imp-fasi-pipeline"><SelectValue placeholder="Pipeline" /></SelectTrigger>
        <SelectContent>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
      </Select>
      {/* Sprint CRM-2B — tabella in colonne allineate; «Tempo massimo» si chiama «Avviso dopo (giorni)» */}
      <div className="grid grid-cols-[14rem_3rem_9rem_9rem_9rem_7rem_1fr] gap-2 items-center px-2 text-xs font-semibold uppercase text-slate-500" data-testid="crm-imp-fasi-intestazione">
        <span>Nome</span><span>Colore</span><span>Tipo</span><span>Avviso dopo (giorni)</span><span>Rilascio dopo (giorni)</span><span>Codice</span><span>Azioni</span></div>
      {fasi.map((f) => {
        const i = normali.indexOf(f);
        return (
          <div key={f.id} className={`grid grid-cols-[14rem_3rem_9rem_9rem_9rem_7rem_1fr] gap-2 items-center border rounded p-2 ${f.archiviata ? "opacity-60 bg-slate-50" : ""}`} data-testid={`crm-imp-fase-${f.codice}`}>
            <Input defaultValue={f.nome} onBlur={(e) => e.target.value !== f.nome && put(f, { nome: e.target.value })} data-testid={`crm-imp-fase-nome-${f.codice}`} />
            <input type="color" value={f.colore || "#64748b"} onChange={(e) => put(f, { colore: e.target.value })} className="h-8 w-8" data-testid={`crm-imp-fase-colore-${f.codice}`} />
            <span className="text-xs text-slate-500">{TIPI_FASE[f.tipo]}</span>
            <Input type="number" min={1} defaultValue={f.tempo_massimo_giorni ?? ""}
              onBlur={(e) => tempi(f, e.target.value === "" ? null : Number(e.target.value), f.giorni_rilascio)} data-testid={`crm-imp-fase-tempo-${f.codice}`} />
            <Input type="number" min={1} defaultValue={typeof f.giorni_rilascio === "number" ? f.giorni_rilascio : ""}
              onBlur={(e) => tempi(f, f.tempo_massimo_giorni, e.target.value === "" ? null : Number(e.target.value))} data-testid={`crm-imp-fase-rilascio-${f.codice}`} />
            <span className="text-xs font-mono text-slate-500" data-testid={`crm-imp-fase-codice-${f.codice}`}>{f.codice}</span>
            <div className="flex flex-wrap gap-1 items-center">
            {errTempi[f.id] && <span className="text-xs text-rose-600" data-testid={`crm-imp-fase-errore-${f.codice}`}>{errTempi[f.id]}</span>}
            <span className="text-xs text-slate-500">{f.n_opportunita} aperte/vinte</span>
            {f.tipo === "normale" && <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => ordina(i, i - 1)}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" disabled={i === normali.length - 1} onClick={() => ordina(i, i + 1)}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => azione(() => api.post(`/crm/fasi/${f.id}/archivia${f.archiviata ? "?riattiva=true" : ""}`), carica)}
                data-testid={`crm-imp-fase-archivia-${f.codice}`}>{f.archiviata ? "Riattiva" : "Archivia"}</Button>
              <Button size="sm" variant="outline" className="text-rose-600" onClick={() => elimina(f)} data-testid={`crm-imp-fase-elimina-${f.codice}`}>Elimina</Button>
            </div>}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-slate-500" data-testid="crm-imp-fasi-spiegazione">Avviso: la scheda si colora dopo N giorni nella fase. Rilascio: dopo N giorni senza attività l'opportunità diventa libera. Vuoto = mai.</div>
      {pid && <div className="flex gap-2 border-t pt-3">
        <Input placeholder="Nome nuova fase" value={nome} onChange={(e) => setNome(e.target.value)} className="w-56" data-testid="crm-imp-nuova-fase-nome" />
        <Button disabled={!nome.trim()} onClick={() => azione(() => api.post(`/crm/pipeline/${pid}/fasi`, { nome }), () => { setNome(""); carica(); })}
          data-testid="crm-imp-nuova-fase-crea">Aggiungi fase</Button>
      </div>}
    </Card>
  );
}

function SezElenco({ cid, tipo }) {
  const confirm = useConfirm();
  const [voci, setVoci] = useState([]);
  const [nome, setNome] = useState("");
  const carica = useCallback(() => api.get(`/crm/elenchi?company_id=${cid}&tipo=${tipo}&gestione=true`).then((r) => setVoci(r.data)).catch(() => setVoci([])), [cid, tipo]);
  useEffect(() => { carica(); }, [carica]);
  const ordina = (i, j) => azione(() => api.post("/crm/elenchi/ordine", { ids: scambia(voci, i, j).map((v) => v.id) }), carica);
  const elimina = async (v) => { if (await confirm({ title: "Elimina voce", message: `Eliminare «${v.nome}»?` })) azione(() => api.delete(`/crm/elenchi/${v.id}`), carica); };
  return (
    <Card className="p-4 space-y-2" data-testid={`crm-imp-elenco-${tipo}`}>
      {voci.map((v, i) => (
        <div key={v.id} className={`flex items-center gap-2 border rounded p-2 ${v.archiviata ? "opacity-60 bg-slate-50" : ""}`} data-testid={`crm-imp-voce-${v.codice}`}>
          <Input defaultValue={v.nome} className="w-64" onBlur={(e) => e.target.value !== v.nome && azione(() => api.put(`/crm/elenchi/${v.id}`, { nome: e.target.value }), carica)} />
          {tipo === "etichetta" && <Tavolozza valore={v.colore} onScegli={(c) => azione(() => api.put(`/crm/elenchi/${v.id}`, { nome: v.nome, colore: c }), carica)} testid={`crm-imp-etichetta-colore-${v.id}`} />}
          <span className="text-xs font-mono text-slate-500">{v.codice}</span>
          {v.archiviata && <span className="text-xs text-slate-500">archiviata</span>}
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => ordina(i, i - 1)}><ArrowUp className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" disabled={i === voci.length - 1} onClick={() => ordina(i, i + 1)}><ArrowDown className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => azione(() => api.post(`/crm/elenchi/${v.id}/archivia${v.archiviata ? "?riattiva=true" : ""}`), carica)}
              data-testid={`crm-imp-voce-archivia-${v.codice}`}>{v.archiviata ? "Riattiva" : "Archivia"}</Button>
            <Button size="sm" variant="outline" className="text-rose-600" onClick={() => elimina(v)} data-testid={`crm-imp-voce-elimina-${v.codice}`}>Elimina</Button>
          </div>
        </div>
      ))}
      <div className="flex gap-2 border-t pt-3">
        <Input placeholder="Nuova voce" value={nome} onChange={(e) => setNome(e.target.value)} className="w-64" data-testid={`crm-imp-nuova-voce-${tipo}`} />
        <Button disabled={!nome.trim()} onClick={() => azione(() => api.post("/crm/elenchi", { company_id: cid, tipo, nome, ...(tipo === "etichetta" ? { colore: TAVOLOZZA_ETICHETTE[0] } : {}) }), () => { setNome(""); carica(); })}
          data-testid={`crm-imp-nuova-voce-crea-${tipo}`}>Aggiungi</Button>
      </div>
    </Card>
  );
}

// Sprint CRM-2B — colori delle etichette da una tavolozza fissa (la stessa del server); CRM-5: ampliata
export const TAVOLOZZA_ETICHETTE = ["#0F4C81", "#B91C1C", "#047857", "#7C3AED", "#C2410C", "#0E7490", "#BE185D", "#4D7C0F", "#1D4ED8", "#92400E",
  "#FACC15", "#F97316", "#84CC16", "#38BDF8", "#A78BFA", "#F472B6", "#64748B", "#CBD5E1"];
function Tavolozza({ valore, onScegli, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {TAVOLOZZA_ETICHETTE.map((c) => <button key={c} type="button" onClick={() => onScegli(c)} title={c} aria-pressed={valore === c}
        className={`h-5 w-5 rounded-full ${valore === c ? "ring-2 ring-offset-1 ring-slate-900" : ""}`} style={{ backgroundColor: c }} />)}
    </div>
  );
}
